import { useState } from "react";
import { toast } from "sonner";
import { MapPin, Mail, ArrowRight } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { contactInfo } from "@/data/content";
import { Reveal, Overline } from "@/components/shared";

export const Contact = () => {
  const { t } = useLang();
  const c = t.contact;
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const data = new FormData();
      data.append("name", form.name);
      data.append("email", form.email);
      data.append("message", form.message);
      const res = await fetch("contact.php", { method: "POST", body: data });
      const json = await res.json().catch(() => ({ ok: false }));
      if (res.ok && json.ok) {
        toast.success(c.form.success);
        setForm({ name: "", email: "", message: "" });
      } else {
        toast.error(c.form.error);
      }
    } catch {
      toast.error(c.form.error);
    } finally {
      setSending(false);
    }
  };

  const details = [
    { icon: MapPin, label: c.labels.address, value: contactInfo.address, href: null },
    { icon: Mail, label: c.labels.email, value: contactInfo.email, href: `mailto:${contactInfo.email}` },
  ];

  return (
    <section id="contact" data-testid="contact" className="border-t border-zinc-800 bg-zinc-950">
      <div className="mx-auto grid max-w-7xl grid-cols-1 gap-16 px-6 py-24 sm:px-12 md:grid-cols-12 md:py-32">
        <div className="md:col-span-5">
          <Reveal>
            <Overline dark>{c.overline}</Overline>
            <h2 className="mt-6 font-heading text-3xl font-light tracking-tight text-white sm:text-5xl">
              {c.title}
            </h2>
            <p className="mt-6 text-base leading-relaxed text-zinc-400">{c.body}</p>
          </Reveal>

          <div className="mt-12 space-y-8">
            {details.map((d, i) => (
              <Reveal key={d.label} i={i}>
                <div className="flex items-start gap-4">
                  <d.icon size={20} strokeWidth={1.5} className="mt-1 shrink-0 text-zinc-500" />
                  <div>
                    <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-600">{d.label}</div>
                    {d.href ? (
                      <a
                        href={d.href}
                        data-testid={`contact-${d.label}`}
                        className="text-base text-zinc-200 transition-colors hover:text-white"
                      >
                        {d.value}
                      </a>
                    ) : (
                      <p className="max-w-xs text-base text-zinc-200">{d.value}</p>
                    )}
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>

        <div className="md:col-span-7">
          <Reveal i={1}>
            <form onSubmit={submit} data-testid="contact-form" className="space-y-8 md:pl-8">
              <div className="grid grid-cols-1 gap-8 sm:grid-cols-2">
                <Field
                  id="contact-name"
                  label={c.form.name}
                  value={form.name}
                  onChange={(v) => setForm({ ...form, name: v })}
                />
                <Field
                  id="contact-email"
                  type="email"
                  label={c.form.email}
                  value={form.email}
                  onChange={(v) => setForm({ ...form, email: v })}
                />
              </div>
              <div>
                <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">
                  {c.form.message}
                </label>
                <textarea
                  required
                  data-testid="contact-message"
                  rows={5}
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  className="mt-3 w-full resize-none border-b border-zinc-700 bg-transparent pb-3 text-white outline-none transition-colors placeholder:text-zinc-700 focus:border-white"
                />
              </div>
              <button
                type="submit"
                data-testid="contact-submit"
                disabled={sending}
                className="group inline-flex items-center gap-3 bg-white px-8 py-4 font-mono text-xs uppercase tracking-[0.2em] text-zinc-900 transition-colors hover:bg-zinc-200 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {sending ? c.form.sending : c.form.submit}
                {!sending && (
                  <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
                )}
              </button>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

const Field = ({ id, label, value, onChange, type = "text" }) => (
  <div>
    <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">{label}</label>
    <input
      required
      type={type}
      data-testid={id}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="mt-3 w-full border-b border-zinc-700 bg-transparent pb-3 text-white outline-none transition-colors focus:border-white"
    />
  </div>
);
